import type { Employee, OfferStateType } from '@prisma/client'
import { Person } from 'app/_components/Avatar'
import type { EntityReference, EntityReferenceType } from 'lib/linkTypes'
import type { Index } from 'meilisearch'
import { prefixIndexId } from './previews'

// Fields needed to render the people directory directly from search hits.
export interface EmployeeDirectoryRecord {
  alias: string | null
  jobTitle: string | null
  email?: string
  phone?: string
  languages: string[]
  domains: string[]
  services: string[]
  groups: string[]
}

export interface OfferDirectoryRecord {
  slug: string
  currentState: OfferStateType
  country: string | null
  office: string | null
  currency: string | null
  estimatedFee: number | null
  hasIncreasedRisk: boolean | null
  services: string[]
  fieldsOfActivity: string[]
  startDate: string | null
  endDate: string | null
  probability: number | null
  costCenter: { abacusNumber: string } | null
  clients: { id: string; name: string }[]
  projectManager: Pick<
    Employee,
    'id' | 'name' | 'firstName' | 'lastName' | 'image'
  > | null
  sponsors: Pick<Employee, 'id' | 'name' | 'firstName' | 'lastName' | 'image'>[]
  deadlines: { name: string; dateTime: number }[]
  projectManagerId: string | null
  sponsorIds: string[]
  portfolioManagerIds: string[]
  participantEmails: string[]
}

export interface IndexSubtypeData {
  employee?: Partial<EmployeeDirectoryRecord>
  offer?: OfferDirectoryRecord
  file?: {
    extension?: string
    size?: string
    sourceUrl?: string
  }
  project?: { costCenters?: string; clients?: string; keywords?: string }
  note?: { folder?: string }
}

// generic model for searchable entities
// original ids are prefixed with the document type
export interface MainIndexRecord extends IndexSubtypeData {
  // reference to the underlying entity
  ref: EntityReference
  // generated from type and localId: the primary key of the search index
  id: string

  createdAt: number | null
  updatedAt: number
  lastSyncedAt?: number

  title: string
  body?: string
  imageUrl?: string

  // an array of the meilisearch-ids (generated from ref using `meilisearchId` specifically as the primary key in the main search index) of related items
  related: string[]
  tags: string[]
  people: Person[]

  // technical properties, not user-visible
  properties?: {
    isPublic?: boolean
  }
}

export type MainIndexInput<
  T extends EntityReferenceType = EntityReferenceType,
> = IndexSubtypeData & {
  // identification
  ref: Extract<EntityReference, { type: T }>

  // metadata
  createdAt: T extends 'employee' ? Date | null : Date
  updatedAt: Date
  deletedAt?: Date | null
  lastSyncedAt?: number

  // content
  title: string
  body?: string | Promise<string | undefined>
  imageUrl?: string
  related?: string[]
  tags?: (string | undefined)[]
  people?: (string | Person)[]

  properties?: {
    isPublic?: boolean
  }
}

// the main search index
export type MainIndex = Index<MainIndexRecord>

export const MAIN_INDEX_ID = prefixIndexId('main')
